import * as React from "react";
import { Search, X } from "lucide-react";
import { cn } from "../lib/utils";

interface MobileHeaderProps {
  title?: string;
  leftAction?: React.ReactNode;
  rightAction?: React.ReactNode;
  className?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: () => void;
}

export function MobileHeader({
  title,
  leftAction,
  rightAction,
  className,
  showSearch = false,
  searchPlaceholder = "検索...",
  searchValue = "",
  onSearchChange,
  onSearchSubmit,
}: MobileHeaderProps) {
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && onSearchSubmit) {
      onSearchSubmit();
    }
    if (e.key === "Escape") {
      setIsSearchOpen(false);
    }
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 border-b bg-background/95 backdrop-blur-md lg:hidden",
        className
      )}
    >
      <div className="flex h-14 items-center justify-between px-4">
        {isSearchOpen ? (
          // Search mode
          <div className="flex flex-1 items-center gap-2">
            <button
              onClick={() => setIsSearchOpen(false)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={searchPlaceholder}
                className="h-10 w-full rounded-full border bg-muted/50 pl-10 pr-4 text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
        ) : (
          // Normal mode
          <>
            <div className="flex items-center gap-2">{leftAction}</div>

            {title && (
              <h1 className="absolute left-1/2 -translate-x-1/2 text-lg font-semibold">{title}</h1>
            )}

            <div className="flex items-center gap-1">
              {showSearch && (
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
                >
                  <Search className="h-5 w-5" />
                </button>
              )}
              {rightAction}
            </div>
          </>
        )}
      </div>

      {/* Airbnb-style pill search bar (optional alternative) */}
      {showSearch && !isSearchOpen && (
        <div className="px-4 pb-3">
          <button
            onClick={() => setIsSearchOpen(true)}
            className="flex w-full items-center gap-3 rounded-full border bg-card px-4 py-3 shadow-sm transition-all hover:shadow-md active:scale-[0.99]"
          >
            <Search className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{searchPlaceholder}</span>
          </button>
        </div>
      )}
    </header>
  );
}
